import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Lock, Unlock, Volume2, VolumeX, Eye, EyeOff, Video as VideoIcon, Music, Type, Plus, Trash2, Flag, Pencil } from 'lucide-react';
import { fmtTime } from './EditorTimelineRuler';
import { EditorAudioWaveform } from './EditorAudioWaveform';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const TRACK_ICON = { video: VideoIcon, audio: Music, text: Type };
const ROW_HEIGHT = 52;
const HEADER_WIDTH = 128;
const MIN_ITEM_DURATION = 0.3;

/**
 * Area 6 — Multitrack Timeline. Real, interactive: drag a source from the
 * Media Panel onto a video track to add it, drag an existing item to
 * reposition it (within its track or onto another compatible track), drag
 * its edges to trim. Split/Delete live in EditorEditingToolbar and act on
 * `selectedItemId`. Track `order` (rendered top = highest layer here)
 * drives EditorPreviewPlayer's compositing.
 *
 * Extended (Main Editor layout redesign) with an Add Track control and a
 * vertically scrollable track area (`tracksHeight`, adjustable from
 * UnifiedEditor.jsx) so the number of tracks is no longer fixed to the six
 * defaults — everything above (drag/trim/snap/zoom/compositing) is
 * unchanged.
 */
export function EditorMultiTrackTimeline({
  composition, siblingSources, playhead, setPlayhead,
  selectedItemId, onSelectItem, zoom, snap,
  onDropNewItem, onMoveItem, onTrimItem, onToggleTrackFlag, onAddTextItem, onWaveformReady,
  onAddTrack, onRemoveTrack, tracksHeight,
  markers, onAddMarker, onRenameMarker, onMoveMarker, onRemoveMarker,
  scrollRef,
}) {
  const [drag, setDrag] = useState(null);
  const [openMarkerId, setOpenMarkerId] = useState(null);
  const [markerDraft, setMarkerDraft] = useState('');
  const rowRefs = useRef({});

  const totalDuration = Math.max(10, composition?.duration_seconds || 10);
  const contentWidth = totalDuration * zoom + 200;
  const snapSeconds = useCallback((s) => (snap ? Math.round(s * 2) / 2 : s), [snap]);
  const sortedTracks = [...(composition?.tracks || [])].sort((a, b) => b.order - a.order);
  const itemsByTrack = (trackId) => (composition?.timeline_items || []).filter((i) => i.track_id === trackId);

  const findTrackAtY = (clientY) => {
    for (const [trackId, el] of Object.entries(rowRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) return trackId;
    }
    return null;
  };

  const startDrag = (e, item, mode, locked) => {
    if (locked) return;
    e.stopPropagation();
    e.preventDefault();
    onSelectItem(item.id);
    setDrag({
      itemId: item.id, mode, startClientX: e.clientX, clientX: e.clientX,
      origStart: item.timeline_start_seconds, origDuration: item.duration_seconds,
      origSourceIn: item.source_in_seconds, origTrackId: item.track_id,
    });
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => setDrag((d) => (d ? { ...d, clientX: e.clientX } : d));
    const onUp = (e) => {
      const deltaSeconds = (e.clientX - drag.startClientX) / zoom;
      if (drag.mode === 'move') {
        const hoverTrackId = findTrackAtY(e.clientY) || drag.origTrackId;
        onMoveItem(drag.itemId, hoverTrackId, snapSeconds(Math.max(0, drag.origStart + deltaSeconds)));
      } else if (drag.mode === 'trim-start') {
        onTrimItem(drag.itemId, 'start', snapSeconds(deltaSeconds));
      } else if (drag.mode === 'trim-end') {
        onTrimItem(drag.itemId, 'end', snapSeconds(deltaSeconds));
      }
      setDrag(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, zoom, onMoveItem, onTrimItem, snapSeconds]);

  const liveOffsetPx = drag ? drag.clientX - drag.startClientX : 0;

  const handleRulerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPlayhead(Math.max(0, (e.clientX - rect.left) / zoom));
  };

  const handleDropOnTrack = (e, track) => {
    e.preventDefault();
    if ((track.type !== 'video' && track.type !== 'audio') || track.locked) return;
    const raw = e.dataTransfer.getData('application/x-consistency-media');
    if (!raw) return;
    const { contentSourceId } = JSON.parse(raw);
    const rect = rowRefs.current[track.id].getBoundingClientRect();
    onDropNewItem(track.id, contentSourceId, snapSeconds(Math.max(0, (e.clientX - rect.left) / zoom)));
  };

  return (
    <div className="glass rounded-xl p-3" data-testid="editor-multitrack-timeline">
      <div className="flex items-center justify-between mb-2 px-0.5">
        <span className="text-[11px] uppercase tracking-wide text-[#555E6E]">Tracks ({sortedTracks.length})</span>
        <div className="flex items-center gap-1.5">
          <button
            className="flex items-center gap-1 text-[11px] text-[#8A93A6] hover:text-white px-2 py-1 rounded-md border border-white/10 hover:border-white/30 transition-colors"
            onClick={() => onAddMarker?.(playhead)}
            data-testid="add-marker-button"
          >
            <Flag className="h-3 w-3" />Add Marker
          </button>
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1 text-[11px] text-[#8A93A6] hover:text-white px-2 py-1 rounded-md border border-white/10 hover:border-white/30 transition-colors" data-testid="add-track-button">
              <Plus className="h-3 w-3" />Add Track
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#14171F] border-white/10 text-white">
            <DropdownMenuItem onClick={() => onAddTrack('video')} data-testid="add-track-video"><VideoIcon className="h-3.5 w-3.5 mr-2" />Video track</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddTrack('audio')} data-testid="add-track-audio"><Music className="h-3.5 w-3.5 mr-2" />Audio track</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddTrack('text')} data-testid="add-track-text"><Type className="h-3.5 w-3.5 mr-2" />Text track</DropdownMenuItem>
          </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="relative" style={{ width: contentWidth, minWidth: '100%' }}>
          <div className="flex">
            <div className="sticky left-0 z-20 shrink-0 bg-[#15171C]" style={{ width: HEADER_WIDTH }} />
            <div onClick={handleRulerClick} className="relative h-6 flex-1 border-b border-white/10 cursor-pointer select-none" data-testid="master-timeline-ruler">
              {Array.from({ length: Math.ceil(totalDuration / 5) + 1 }).map((_, i) => (
                <span key={i} className="absolute top-0 text-[10px] text-[#555E6E]" style={{ left: i * 5 * zoom }}>{fmtTime(i * 5)}</span>
              ))}
              {(markers || []).map((m) => (
                <Popover key={m.id} open={openMarkerId === m.id} onOpenChange={(o) => { setOpenMarkerId(o ? m.id : null); if (o) setMarkerDraft(m.label); }}>
                  <PopoverTrigger asChild>
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenMarkerId(m.id); setMarkerDraft(m.label); }}
                      className="absolute top-0 -translate-x-1/2 text-amber-400 hover:text-amber-300 z-20"
                      style={{ left: m.elapsed_seconds * zoom }}
                      title={m.label}
                      data-testid={`master-marker-flag-${m.id}`}
                    >
                      <Flag className="h-3.5 w-3.5 fill-current" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 bg-[#14171F] border-white/10 text-white p-2.5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`master-marker-popover-${m.id}`}>
                    <p className="text-[10px] text-[#555E6E]">{fmtTime(m.elapsed_seconds)}</p>
                    <Input
                      value={markerDraft} onChange={(e) => setMarkerDraft(e.target.value)}
                      className="h-7 text-xs bg-black/40 border-white/10" data-testid={`master-marker-label-input-${m.id}`}
                    />
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10 flex-1" onClick={() => { onRenameMarker?.(m.id, markerDraft); setOpenMarkerId(null); }} data-testid={`master-marker-save-${m.id}`}>
                        <Pencil className="h-3 w-3 mr-1" />Save
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={() => onMoveMarker?.(m.id, playhead)} title="Move to playhead" data-testid={`master-marker-move-${m.id}`}>
                        Move Here
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-500/30 text-red-400" onClick={() => { onRemoveMarker?.(m.id); setOpenMarkerId(null); }} data-testid={`master-marker-delete-${m.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              ))}
            </div>
          </div>

          <div style={tracksHeight ? { maxHeight: tracksHeight, overflowY: 'auto' } : undefined} data-testid="master-timeline-tracks-scroll">
          {sortedTracks.map((track) => {
            const Icon = TRACK_ICON[track.type] || VideoIcon;
            const itemCount = itemsByTrack(track.id).length;
            return (
              <div key={track.id} className="flex items-stretch border-b border-white/5" style={{ height: ROW_HEIGHT }}>
                <div className="sticky left-0 z-20 shrink-0 bg-[#15171C] border-r border-white/10 flex items-center justify-between px-2" style={{ width: HEADER_WIDTH }} data-testid={`master-track-header-${track.id}`}>
                  <span className="flex items-center gap-1.5 text-xs text-white truncate"><Icon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />{track.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {track.type !== 'text' && (
                      <button onClick={() => onToggleTrackFlag(track.id, track.type === 'audio' ? 'muted' : 'hidden')} className="text-[#8A93A6] hover:text-white" data-testid={`master-track-toggle-${track.id}`}>
                        {track.type === 'audio' ? (track.muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />) : (track.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />)}
                      </button>
                    )}
                    <button onClick={() => onToggleTrackFlag(track.id, 'locked')} className="text-[#8A93A6] hover:text-white" data-testid={`master-track-lock-${track.id}`}>
                      {track.locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                    </button>
                    {track.custom && itemCount === 0 && (
                      <button onClick={() => onRemoveTrack(track.id)} className="text-[#8A93A6] hover:text-red-400" title="Remove empty track" data-testid={`master-track-remove-${track.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div
                  ref={(el) => { rowRefs.current[track.id] = el; }}
                  onDragOver={(e) => { if ((track.type === 'video' || track.type === 'audio') && !track.locked) e.preventDefault(); }}
                  onDrop={(e) => handleDropOnTrack(e, track)}
                  onDoubleClick={(e) => {
                    if (track.type !== 'text' || track.locked) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    onAddTextItem(track.id, snapSeconds(Math.max(0, (e.clientX - rect.left) / zoom)));
                  }}
                  className="relative flex-1"
                  data-testid={`master-track-row-${track.id}`}
                >
                  {itemsByTrack(track.id).map((item) => {
                    const isDraggingThis = drag?.itemId === item.id;
                    let left = item.timeline_start_seconds * zoom;
                    let width = Math.max(6, item.duration_seconds * zoom);
                    if (isDraggingThis && drag.mode === 'move') left = Math.max(0, drag.origStart * zoom + liveOffsetPx);
                    if (isDraggingThis && drag.mode === 'trim-start') {
                      const d = Math.max(-drag.origSourceIn, Math.min(drag.origDuration - MIN_ITEM_DURATION, liveOffsetPx / zoom));
                      left = (drag.origStart + d) * zoom;
                      width = Math.max(6, (drag.origDuration - d) * zoom);
                    }
                    if (isDraggingThis && drag.mode === 'trim-end') {
                      const d = Math.max(-(drag.origDuration - MIN_ITEM_DURATION), liveOffsetPx / zoom);
                      width = Math.max(6, (drag.origDuration + d) * zoom);
                    }
                    const source = siblingSources.find((s) => s.id === item.content_source_id);
                    const selected = selectedItemId === item.id;
                    const isCaptionBlock = item.item_type === 'text' && (item.text?.segments?.length || 0) > 0;
                    const hasTransition = item.item_type === 'video' && item.transition_in && item.transition_in.type !== 'cut';
                    return (
                      <div
                        key={item.id}
                        onMouseDown={(e) => startDrag(e, item, 'move', track.locked)}
                        onClick={(e) => { e.stopPropagation(); onSelectItem(item.id); }}
                        className={`absolute top-1.5 bottom-1.5 rounded-md border-2 px-2 flex items-center overflow-hidden ${track.locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'} ${
                          selected ? 'border-[#3A7BFF] bg-[#3A7BFF]/30 z-10' : item.item_type === 'text' ? 'border-amber-400/40 bg-amber-400/15' : item.item_type === 'audio' ? 'border-emerald-400/40 bg-emerald-400/15' : 'border-white/20 bg-white/10 hover:border-white/40'
                        }`}
                        style={{ left, width }}
                        data-testid={`master-timeline-item-${item.id}`}
                      >
                        {hasTransition && (
                          <div className="absolute left-0 top-0 bottom-0 w-2 bg-fuchsia-400/60 pointer-events-none" data-testid={`master-item-transition-marker-${item.id}`} />
                        )}
                        <div className="flex flex-col w-full h-full justify-center overflow-hidden pointer-events-none">
                          <span className="text-[11px] text-white truncate font-medium flex items-center gap-1">
                            {item.item_type === 'text' ? (isCaptionBlock ? 'Captions' : (item.text?.text || 'Text')) : (source?.title || item.content_source_id)}
                            {item.is_pip && <span className="shrink-0 text-[8px] px-1 rounded bg-[#3A7BFF]/50" data-testid={`master-item-pip-badge-${item.id}`}>PiP</span>}
                            {(item.speed_percent ?? 100) !== 100 && (
                              <span className="shrink-0 text-[8px] px-1 rounded bg-white/20" data-testid={`master-item-speed-badge-${item.id}`}>{((item.speed_percent ?? 100) / 100).toFixed(2)}x</span>
                            )}
                            {isCaptionBlock && (
                              <span className="shrink-0 text-[8px] px-1 rounded bg-sky-400/30 text-sky-200" data-testid={`master-item-caption-badge-${item.id}`}>CC·{item.text.segments.length}</span>
                            )}
                            {hasTransition && (
                              <span className="shrink-0 text-[8px] px-1 rounded bg-fuchsia-400/30 text-fuchsia-200" data-testid={`master-item-transition-badge-${item.id}`}>{item.transition_in.type.replace('_', ' ')}</span>
                            )}
                            {item.item_type === 'video' && (item.effects?.length > 0) && (
                              <span className="shrink-0 text-[8px] px-1 rounded bg-lime-400/30 text-lime-200" data-testid={`master-item-effects-badge-${item.id}`}>FX·{item.effects.length}</span>
                            )}
                            {item.item_type === 'video' && (item.blur_regions?.length > 0) && (
                              <span className="shrink-0 text-[8px] px-1 rounded bg-orange-400/30 text-orange-200" data-testid={`master-item-blur-badge-${item.id}`}>Blur·{item.blur_regions.length}</span>
                            )}
                          </span>
                          {item.item_type === 'audio' && (
                            <EditorAudioWaveform item={item} streamUrl={source?.stream_url} onWaveformReady={(wf) => onWaveformReady?.(item.id, wf)} />
                          )}
                        </div>
                        {!track.locked && (
                          <>
                            <div onMouseDown={(e) => startDrag(e, item, 'trim-start', false)} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-white/20 hover:bg-white/50" data-testid={`master-trim-start-${item.id}`} />
                            <div onMouseDown={(e) => startDrag(e, item, 'trim-end', false)} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-white/20 hover:bg-white/50" data-testid={`master-trim-end-${item.id}`} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </div>

          <div className="absolute top-0 bottom-0 w-0.5 bg-pink-500 z-30 pointer-events-none" style={{ left: HEADER_WIDTH + playhead * zoom }} data-testid="master-timeline-playhead" />
        </div>
      </div>
    </div>
  );
}
